import * as React from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Button, Grid, Title, SegmentedControl, useMantineTheme, Divider, Flex, Stack } from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";

import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { CTextarea, CTextInput } from "@supportinitiative/react-hook-form-mantine";
import { UseCaseType } from "@supportinitiative/common";
import { InferType } from "yup";

import { apiAxios, getAuthHeader, getLocalStorage } from "../api";
import { AuditType } from "../types";
import { getActionOptions, UseCaseSchema } from "./UseCaseUtils";
import { UserContext } from "../misc/UserContext";
import { useUseCase } from "../hooks";
import FormSubmitError from "../ui/Error/FormSubmitError";
import FormStateError from "../ui/Error/FormStateError";
import Success from "../ui/Success";
import Audit from "../ui/Audit/Audit";

import { styles } from "src/styles";
import { initialState, reducer, UseCaseCVMContext } from "./UseCaseCVMContext";
import PreferencesCVM from "./PreferencesCVM";
import OrchestratorCVM from "./OrchestratorCVM";
import { getDefaultWorkflow } from "./Workflow/WorkflowUtils";
import ReconciliationCVM from "./Reconciliation/ReconciliationCVM";
import DashboardsAndActionsCVM from "./DashboardsAndActionsCVM";

type MyInferredType = InferType<typeof UseCaseSchema>;

async function postUseCase(useCase: any) {
  return await apiAxios.post("/api/usecase", useCase, {
    params: {
      __entityId: useCase.entityId,
    },
    headers: {
      Authorization: getAuthHeader(),
    },
  });
}

// TODO: Deletion

export default function UseCaseCVM() {
  let [audit, setAudit] = React.useState<AuditType | undefined>();
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: useCase } = useUseCase(entityId, id);
  const [activeTab, setActiveTab] = React.useState<string>("0");
  const [userStatus, setUserStatus] = React.useState<{ label: string; value: string }[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UseCaseType>({
    defaultValues: {
      name: "",
      entityId: "",
      displayName: "",
      description: "",
      definition: {
        masterDataset: {
          id: "MASTER",
          type: "DATASET",
          name: "Master Dataset",
        },
        loaderId: "",
        userStatus: [],
        dashboards: [],
        inputs: [],
        preferences: {
          run: "ONCE",
          runManagement: "MANUAL",
          match: "HIERARCHICAL",
        },
        orchestrator: {
          workflow: getDefaultWorkflow(),
        },
        recons: [],
      },
    },
    criteriaMode: "all",
    // resolver: yupResolver(UseCaseSchema),
    resolver: yupResolver(UseCaseSchema),
  });

  const [useCaseFormState, useCaseFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    entityId,
    actionOptions: getActionOptions("new", useCase, useCase, applicationState),
    setValue: setValue,
  });

  const reconsArray = useFieldArray({
    control,
    name: "definition.recons",
    keyName: "key",
  });

  const recons = useWatch({
    control,
    name: "definition.recons",
  });

  const onOk = () => {
    navigate(`/app/useCases?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (useCase) {
      useCaseFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      useCaseFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions("view", useCase, useCase, applicationState),
      });
      setAudit({
        status: useCase.status,
        authStatus: useCase.authStatus,
        usableStatus: useCase.usableStatus,
        makerId: useCase.makerId,
        makerDate: useCase.makerDate,
        modificationNo: useCase.modificationNo,
        checkerId: useCase.checkerId,
        checkerDate: useCase.checkerDate,
      });

      let userStatus = (useCase.definition.userStatus || []).map((status) => {
        return { label: status, value: status };
      });
      setUserStatus(userStatus || []);
      useCaseFormDispatch({
        type: "SET-USER-STATUS",
        value: userStatus,
      });
      reset(useCase);
    }
  }, [useCase]);

  const onSubmit = async (data: any) => {
    try {
      useCaseFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      useCaseFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metadata: {
          action: useCaseFormState.currentAction,
        },
      };
      await postUseCase(payload);
      useCaseFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["useCase", entityId, id]);
      queryClient.invalidateQueries(["useCases", entityId]);
      useCaseFormDispatch({
        type: "SET-SUCCESS",
        value: "Use case saved successfully",
      });
    } catch (error: any) {
      useCaseFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      useCaseFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save use case",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: theme.spacing.lg }} noValidate>
      <UseCaseCVMContext.Provider value={{ state: useCaseFormState, dispatch: useCaseFormDispatch }}>
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    Use Case
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>
        <Divider size="sm" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={2}>
              <CTextInput
                name="name"
                control={control}
                label="Name"
                required
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <CTextInput
                name="displayName"
                control={control}
                label="Display Name"
                required
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <div
            style={{
              marginTop: theme.spacing.xl,
              marginBottom: theme.spacing.xs,
            }}
          >
            <SegmentedControl
              value={activeTab}
              onChange={setActiveTab}
              data={[
                {
                  label: `Preferences`,
                  value: "0",
                },
                {
                  label: `Orchestrator`,
                  value: "1",
                },
                {
                  label: `Dashboards & Actions`,
                  value: "2",
                },
                /*                 {
                  label: `Trigger Flows`,
                  value: "3",
                }, */
                ...recons.map((recon, index) => {
                  return {
                    label: `${recon.name ? `${recon.name} (${recon.id})` : `Recon ${index + 1}`}`,
                    value: (index + 4).toString(),
                  };
                }),
              ]}
            />

            {activeTab === "0" ? (
              <PreferencesCVM
                name="definition"
                control={control}
                userStatus={userStatus}
                setUserStatus={setUserStatus}
              />
            ) : activeTab === "1" ? (
              <OrchestratorCVM name="definition.orchestrator" control={control} reconsArray={reconsArray} />
            ) : activeTab === "2" ? (
              <DashboardsAndActionsCVM name="definition" control={control} />
            ) : (
              <>
                {reconsArray.fields.map((field, index) => {
                  if ((index + 4).toString() === activeTab) {
                    if (field.type === "RECONCILIATION" || field.type === "MONITORING") {
                      return (
                        <ReconciliationCVM
                          id={field.id}
                          name={`definition.recons.${index}`}
                          control={control}
                          key={field.key}
                          index={index}
                          reconsArray={reconsArray}
                          setActiveTab={setActiveTab}
                          fromWorkflowName="definition.orchestrator"
                        />
                      );
                    }
                  }
                })}
              </>
            )}
          </div>
        </div>

        {(Object.keys(errors).length > 0 || useCaseFormState.error) && (
          <div style={styles.errors} id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={useCaseFormState.error} />
            </div>
          </div>
        )}
        <div
          id="actions"
          style={{
            marginTop: theme.spacing.lg,
            marginBottom: theme.spacing.lg,
          }}
        >
          <Grid align="stretch" gutter={"xs"} columns={24}>
            <>
              {(useCaseFormState.actionOptions.allowedActions || []).map((action: string) => {
                if (action.toUpperCase() === "SAVE") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button color="teal" variant="filled" fullWidth type="submit">
                        Save
                      </Button>
                    </Grid.Col>
                  );
                } else if (action.toUpperCase() === "CANCEL") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button variant="default" fullWidth component={Link} to={`/app/useCases?entityId=${entityId}`}>
                        Cancel
                      </Button>
                    </Grid.Col>
                  );
                } else if (action.toUpperCase() === "EDIT") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        onClick={() => {
                          useCaseFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "EDIT",
                          });

                          useCaseFormDispatch({
                            type: "ACTION-OPTIONS",
                            value: getActionOptions("edit", useCase, useCase, applicationState),
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Grid.Col>
                  );
                } else if (action.toUpperCase() === "AUTH") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        onClick={async () => {
                          useCaseFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "auth",
                          });
                          try {
                            let payload = {
                              ...useCase,
                              entityId: getLocalStorage("currentEntity"),
                              metaData: {
                                action: "auth",
                              },
                            };
                            await postUseCase(payload);
                            // setError("Form", "Unable to Submit");
                            useCaseFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "view",
                            });

                            useCaseFormDispatch({
                              type: "SET-SUCCESS",
                              value: "UseCase Authorized Successfully",
                            });
                          } catch (error: any) {
                            useCaseFormDispatch({
                              type: "SET-SUBMITTING",
                              value: false,
                            });
                            useCaseFormDispatch({
                              type: "SET-ERROR",
                              value: error?.response?.data?.message || "Unable to Authorize UseCase",
                            });
                          }
                        }}
                      >
                        Auth
                      </Button>
                    </Grid.Col>
                  );
                } else if (action.toUpperCase() === "DELETE") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        onClick={() => {
                          useCaseFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "delete",
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </Grid.Col>
                  );
                } else {
                  return null;
                }
              })}
            </>
          </Grid>
        </div>

        <Audit audit={audit} />
        {useCaseFormState.successMessage && <Success message={useCaseFormState.successMessage} onOk={onOk} />}
      </UseCaseCVMContext.Provider>
    </form>
  );
}
